import { useState } from "react";
import useMerchantPortalAxios, {
  useMerchantPortalFormData,
} from "../../utils/axios";
import axios from "axios";
import {
  setCreateQuickPayRequest,
  setQuickPayDesign,
  setQuickPayDetails,
  setQuickPayList,
  setQuickPayReduxList,
  setQuickPayTemplate,
  setShowQuickPayDetails,
} from "../../features/QuickPaySlice";
import { setRequestData } from "../../features/transactionDetailsSlice";
import { useDispatch } from "react-redux";
import {
  setError as setAppError,
  setFeatureData,
  setTableData,
  setTableLoading,
} from "../store/slices/appSlice";
import { config } from "../../utils/Constants";

export default function useNewQuickPayAPI() {
  const client = useMerchantPortalAxios();
  const formClient = useMerchantPortalFormData();
  const dispatch = useDispatch();
  const baseUrl = config.url;
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);

  client.defaults.timeout = 600000;
  formClient.defaults.timeout = 600000;

  async function GetQuickPayTemplate() {
    const response = await client.get("/v1/QuickPay/GetQuickPayTemplate");
    const payload = response?.data?.Payload || response?.data || "";
    dispatch(setQuickPayTemplate(payload));
    dispatch(
      setFeatureData({
        section: "details",
        key: "quickPay",
        value: {
          template: payload,
        },
      }),
    );
    return payload;
  }

  async function GetQuickPayDesign() {
    setLoading(true);

    try {
      const response = await client.post("/v1/QuickPay/GetQuickPayDesign", {
        SessionID: sessionStorage.getItem("SessionID"),
      });
      const payload = response?.data?.Payload?.UploadInterface;
      dispatch(setQuickPayDesign(payload));
      dispatch(
        setFeatureData({
          section: "details",
          key: "quickPay",
          value: {
            uploadInterface: payload,
          },
        }),
      );
      return payload;
    } finally {
      setLoading(false);
    }
  }

  async function CreateQuickPayRequest() {
    setLoading(true);

    try {
      const response = await client.post("/v1/QuickPay/CreateQuickPayRequest", {
        SessionID: sessionStorage.getItem("SessionID"),
      });
      const payload = response?.data?.Payload || {};
      dispatch(setCreateQuickPayRequest(payload));
      dispatch(
        setFeatureData({
          section: "ui",
          key: "quickPay",
          value: {
            createRequest: payload,
          },
        }),
      );
      return payload;
    } finally {
      setLoading(false);
    }
  }

  async function GetQuickPayList() {
    setLoading(true);
    dispatch(setTableLoading({ key: "quickPay", loading: true }));
    dispatch(setAppError(null));

    try {
      const response = await client.post("/v1/QuickPay/GetQuickPayList", {
        SessionID: sessionStorage.getItem("SessionID"),
      });
      const payload = response?.data?.Payload?.quickPays || [];
      dispatch(setQuickPayList(payload));
      dispatch(
        setTableData({
          key: "quickPay",
          data: payload,
          pagination: {
            page: 1,
            totalPages: 1,
            totalCount: payload.length,
          },
        }),
      );
      return payload;
    } catch (error) {
      dispatch(
        setTableData({
          key: "quickPay",
          data: [],
          pagination: {
            page: 1,
            totalPages: 0,
            totalCount: 0,
          },
        }),
      );
      dispatch(
        setAppError(
          error?.response?.data?.Message ||
            error?.response?.data?.message ||
            "Unable to load quick pay list.",
        ),
      );
      throw error;
    } finally {
      setLoading(false);
      dispatch(setTableLoading({ key: "quickPay", loading: false }));
    }
  }

  async function GetQuickPayDetails(identifier, status) {
    setDetailsLoading(true);

    try {
      const response = await client.post("/v1/QuickPay/GetQuickPayDetails", {
        SessionID: sessionStorage.getItem("SessionID"),
        Identifier: identifier,
        Status: status,
      });

      const payload = response?.data?.Payload || {};
      dispatch(
        setQuickPayReduxList({
          list: payload?.corporateQuickPays || [],
          status: payload?.Status || status || "",
        }),
      );
      dispatch(
        setFeatureData({
          section: "details",
          key: "quickPay",
          value: {
            selectedDetails: payload,
            selectedIdentifier: identifier,
            selectedStatus: status,
          },
        }),
      );
      dispatch(
        setTableData({
          key: "quickPay",
          data: payload?.corporateQuickPays || [],
          pagination: {
            page: 1,
            totalPages: 1,
            totalCount: Array.isArray(payload?.corporateQuickPays)
              ? payload.corporateQuickPays.length
              : 0,
          },
        }),
      );

      return payload;
    } finally {
      setDetailsLoading(false);
    }
  }

  async function UploadQuickPay(payload) {
    const nextPayload = { ...payload };

    if (nextPayload.formFile) {
      const fileData = nextPayload.formFile.split(",")[1];
      const mimeType = nextPayload.formFile.split(":")[1].split(";")[0];
      const byteCharacters = atob(fileData);
      const byteArrays = new Uint8Array(byteCharacters.length);

      for (let index = 0; index < byteCharacters.length; index += 1) {
        byteArrays[index] = byteCharacters.charCodeAt(index);
      }

      nextPayload.formFile = new File(
        [byteArrays],
        nextPayload?.ReasonFileName,
        { type: mimeType },
      );
    }

    const { ReasonFileName, ...requestPayload } = nextPayload;
    const response = await formClient.post(
      "/v1/QuickPay/UploadQuickPay",
      requestPayload,
    );
    return response.data;
  }

  async function PushQuickPay(payload) {
    const response = await client.post("/v1/QuickPay/PushQuickPay", payload);
    return response?.data?.Payload;
  }

  async function PushNewQuickPayEmployees(payload) {
    const response = await client.post(
      "/v1/QuickPay/PushNewQuickPayEmployees",
      payload,
    );
    return response?.data?.Payload;
  }

  async function DeletePendingApprovalQuickPay(payload) {
    const response = await client.post(
      "/v1/QuickPay/DeletePendingApprovalQuickPay",
      payload,
    );
    return response?.data?.Payload;
  }

  async function DeleteActiveQuickPay(payload) {
    const response = await client.post(
      "/v1/QuickPay/DeleteActiveQuickPay",
      payload,
    );
    return response?.data?.Payload;
  }

  async function DeleteSingleUserQuickPay(payload) {
    const response = await client.post(
      "/v1/QuickPay/DeleteSingleUserQuickPay",
      payload,
    );
    return response?.data?.Payload;
  }

  async function EditSingleUserQuickPay(payload) {
    const response = await client.post(
      "/v1/QuickPay/EditSingleUserQuickPay",
      payload,
    );
    return response?.data?.Payload;
  }

  async function UploadNewQuickPayEmployees(payload) {
    const nextPayload = { ...payload };

    if (nextPayload.formFile) {
      const fileData = nextPayload.formFile.split(",")[1];
      const mimeType = nextPayload.formFile.split(":")[1].split(";")[0];
      const byteCharacters = atob(fileData);
      const byteArrays = new Uint8Array(byteCharacters.length);

      for (let index = 0; index < byteCharacters.length; index += 1) {
        byteArrays[index] = byteCharacters.charCodeAt(index);
      }

      nextPayload.formFile = new File(
        [byteArrays],
        nextPayload?.ReasonFileName,
        { type: mimeType },
      );
    }

    const { ReasonFileName, ...requestPayload } = nextPayload;
    const response = await formClient.post(
      "/v1/QuickPay/UploadNewQuickPayEmployees",
      requestPayload,
    );
    return response.data;
  }

  async function GetQuickPaySummary(checkerAdvanceId) {
    const normalizedCheckerAdvanceId = Number(checkerAdvanceId);
    const response = await client.post(
      "/v1/QuickPay/GetPendingQuickPayDetails",
      {
        SessionID: sessionStorage.getItem("SessionID"),
        CheckerAdvanceId: Number.isNaN(normalizedCheckerAdvanceId)
          ? checkerAdvanceId
          : normalizedCheckerAdvanceId,
      },
    );

    dispatch(setShowQuickPayDetails(true));
    dispatch(setQuickPayDetails(response?.data?.Payload?.quickPayDetailsList || []));
    dispatch(setRequestData({ field: "visible", value: false }));
    dispatch(
      setFeatureData({
        section: "details",
        key: "quickPay",
        value: {
          pendingQuickPayDetails: response?.data?.Payload?.quickPayDetailsList || [],
          summaryPayload: response?.data?.Payload || {},
        },
      }),
    );
    return response?.data?.Payload || {};
  }

  async function GetQuickPaySummaryMobile(checkerAdvanceId) {
    const response = await axios.post(
      `${baseUrl}/v1/QuickPay/GetPendingQuickPayDetails`,
      {
        SessionID: sessionStorage.getItem("SessionMobile"),
        CheckerAdvanceId: checkerAdvanceId,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionStorage.getItem("TokenMobile"),
        },
      },
    );

    dispatch(setShowQuickPayDetails(true));
    dispatch(setQuickPayDetails(response?.data?.Payload?.quickPayDetailsList || []));
    dispatch(setRequestData({ field: "visible", value: false }));
    return response?.data?.Payload || {};
  }

  async function ApproveRejectQuickPayMobile(payload) {
    const response = await axios.post(
      `${baseUrl}/v1/QuickPay/ApproveRejectPendingQuickPayDetails`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionStorage.getItem("TokenMobile"),
        },
      },
    );

    return response?.data?.Payload;
  }

  return {
    CreateQuickPayRequest,
    DeleteActiveQuickPay,
    DeletePendingApprovalQuickPay,
    DeleteSingleUserQuickPay,
    EditSingleUserQuickPay,
    GetQuickPayDesign,
    GetQuickPayDetails,
    GetQuickPayList,
    GetQuickPaySummary,
    GetQuickPaySummaryMobile,
    GetQuickPayTemplate,
    ApproveRejectQuickPayMobile,
    PushNewQuickPayEmployees,
    PushQuickPay,
    UploadNewQuickPayEmployees,
    UploadQuickPay,
    detailsLoading,
    loading,
  };
}
